import {useEffect, useMemo, useRef, useState} from "react";
import {SoftFogWorkerClient} from "./SoftFogWorkerClient.ts";
import type {FogScene} from "./SoftFogWorkerProtocol.ts";
import {animateFogLights} from "./FogLightAnimation.ts";
import {fogSceneAtResolution} from "./FogRenderResolution.ts";

export type FogFrame = {scene: FogScene; bitmap: ImageBitmap; tintBitmap: ImageBitmap};

export function useSoftFogWorker(scene: FogScene | undefined): {
    frame: FogFrame | undefined;
    unavailable: boolean;
} {
    const renderScene = useMemo(() => scene && fogSceneAtResolution(scene), [scene]);
    const clientRef = useRef<SoftFogWorkerClient | null>(null);
    const [frame, setFrame] = useState<FogFrame | undefined>();
    const [unavailable, setUnavailable] = useState(
        () => typeof Worker === "undefined" || typeof OffscreenCanvas === "undefined",
    );

    useEffect(() => {
        if (unavailable) return;
        try {
            const worker = new Worker(new URL("./SoftFog.worker.ts", import.meta.url), {type: "module"});
            const client = new SoftFogWorkerClient(worker,
                (completedScene, bitmap, tintBitmap) => setFrame({scene: completedScene, bitmap, tintBitmap}),
                () => {
                    clientRef.current = null;
                    setFrame(undefined);
                    setUnavailable(true);
                });
            clientRef.current = client;
            return () => {
                client.dispose();
                clientRef.current = null;
            };
        } catch {
            // Without WebGL fog, the enabled player map stays dark.
            queueMicrotask(() => setUnavailable(true));
            return;
        }
    }, [unavailable]);

    useEffect(() => {
        const client = clientRef.current;
        if (!client) return;
        if (!renderScene) {client.invalidate(); return;}
        client.request(renderScene);
        return animateFogLights(renderScene, value => client.request(value));
    }, [renderScene, unavailable]);

    useEffect(() => {
        const bitmap = frame?.bitmap;
        if (!bitmap) return;
        return () => {
            bitmap.close();
            frame.tintBitmap.close();
        };
    }, [frame]);

    return {frame, unavailable};
}
